import axios, { CancelTokenSource } from 'axios'
import { createContext, PropsWithChildren, useContext, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCancelUploadFileApi } from 'api/files/mutations/useCancelUploadFileApi'
import { useGenerateFileUploadUrlApi } from 'api/files/mutations/useGenerateFileUploadUrlApi'
import { useUploadFileApi } from 'api/files/mutations/useUploadFileApi'
import { isConflictError } from 'api/utils'
import { FILE_IS_INFECTED_ERROR, useCheckFileUploaded } from 'pages/files/uploadFileModal/utils'
import { useToast } from 'providers/toast/ToastProvider'
import { AssignmentType, FileItem } from 'types/files/files'

interface FileUpload {
  file: File
  cancelTokenSource: CancelTokenSource
  fileInfo: FileItem
  error?: string
}

interface UploadFileProps {
  file: File
  folderId: string | undefined
  assignmentId: string
  assignmentType: AssignmentType
  allowInAiAssistant: boolean
}

interface FileUploadContextProps {
  uploads: FileUpload[]
  uploadFile: (props: UploadFileProps) => Promise<void>
  cancelUpload: (index: number) => void
}

export const FileUploadContext = createContext<FileUploadContextProps | undefined>(undefined)

export const FileUploadProvider = ({ children }: PropsWithChildren<{}>) => {
  const [uploads, setUploads] = useState<FileUpload[]>([])
  const { t } = useTranslation()
  const { enqueueToast } = useToast()
  const cancelTokens = useRef<Map<number, CancelTokenSource>>(new Map())

  const { mutateAsync: generateFileUploadUrl } = useGenerateFileUploadUrlApi()
  const { checkFileUploaded, cancelCheckFileUploaded } = useCheckFileUploaded()
  const { mutateAsync: handleUploadFile } = useUploadFileApi()
  const { mutateAsync: cancelUploadFile } = useCancelUploadFileApi()

  const cancelUpload = async (index: number) => {
    const source = cancelTokens.current.get(index)
    if (source) {
      source.cancel('File upload canceled')
      cancelTokens.current.delete(index)
      setUploads(prevUploads => prevUploads.filter((_, i) => i !== index))
      cancelCheckFileUploaded()
      await cancelUploadFile(uploads[index].fileInfo.id).catch(() => {})
      enqueueToast({
        message: t('os.files.upload_file_modal.file_upload_canceled'),
        type: 'success',
      })
    }
  }

  const uploadFile = async ({ file, folderId, assignmentId, assignmentType, allowInAiAssistant }: UploadFileProps) => {
    const cancelTokenSource = axios.CancelToken.source()
    const index = uploads.length // Use current length as index for simplicity

    try {
      const data = {
        parentId: folderId,
        assignmentId,
        assignmentType,
        name: file.name,
        contentType: file.type || 'application/octet-stream',
        allowInAiAssistant,
      }

      const { data: fileData } = await generateFileUploadUrl(data)
      const { uploadInfo, fileInfo } = fileData

      // Register the cancel token with the context
      cancelTokens.current.set(index, cancelTokenSource)

      // Add the file to the list of uploads
      setUploads(prevUploads => [...prevUploads, { file, cancelTokenSource, fileInfo }])

      await handleUploadFile({ uploadInfo, file, source: cancelTokenSource })
      await checkFileUploaded(fileInfo.id)

      // Remove the file from the list of uploads upon successful upload
      setUploads(prevUploads => prevUploads.filter((_, i) => i !== index))
      enqueueToast({
        message: t('os.files.upload_file_modal.file_uploaded', { fileName: file.name }),
        type: 'success',
      })
    } catch (e) {
      // console.log('Upload error => ', e)
      if (axios.isCancel(e)) return
      let error: string | undefined
      if (isConflictError(e)) {
        error = t('os.files.upload_file_modal.same_name_file_error')
      }
      switch ((e as Error).message) {
        case FILE_IS_INFECTED_ERROR:
          error = t('os.files.upload_file_modal.file_is_infected')
          break
        default:
          error = t('os.files.upload_file_modal.file_failed_to_upload')
          break
      }
      error && setUploads(prevUploads => prevUploads.map((upload, i) => (i === index ? { ...upload, error } : upload)))
      return
    }
  }

  return (
    <FileUploadContext.Provider value={{ uploads, uploadFile, cancelUpload }}>{children}</FileUploadContext.Provider>
  )
}

export const useFileUploadContext = () => {
  const context = useContext(FileUploadContext)
  if (!context) {
    throw new Error('useFileUploadContext must be used within a FileUploadProvider')
  }
  return context
}
